import { useGameContext } from '../contexts/GameContext'
import { useUIContext } from '../contexts/UIContext'
import { useSelector } from 'react-redux'
import { useMemo } from 'react'

/**
 * Hook personalizado que combina el estado del juego de Redux con los contextos modernos
 * Proporciona una API unificada para acceder al estado del juego
 */
export const useGame = () => {
  const gameContext = useGameContext()
  const uiContext = useUIContext()

  // Selectores de Redux críticos (mantienen compatibilidad)
  const reduxState = useSelector(state => ({
    currentScene: state.game?.currentScene,
    isLoading: state.loader?.isLoading,
    progress: state.loader?.progress,
    showingError: state.game?.showingError,
    userInfo: state.auth?.userInfo
  }))

  // Combinar todo el estado en un objeto memoizado
  const gameState = useMemo(() => ({
    // Estado de Redux (crítico)
    scene: reduxState.currentScene,
    isLoading: reduxState.isLoading,
    progress: reduxState.progress,
    showingError: reduxState.showingError,
    user: reduxState.userInfo,

    // Estado del contexto del juego (configuración)
    settings: {
      performanceMode: gameContext.performanceMode,
      accessibilityMode: gameContext.accessibilityMode,
      theme: gameContext.theme,
      soundEnabled: gameContext.soundEnabled,
      debugMode: gameContext.debugMode,
      isFullscreen: gameContext.isFullscreen,
      language: gameContext.language
    },

    // Estado del viewport
    viewport: gameContext.viewport,

    // Estado de UI (interacciones)
    ui: {
      modal: uiContext.modal,
      loading: uiContext.loading,
      notification: uiContext.notification,
      cursor: uiContext.cursor,
      dragState: uiContext.dragState,
      menu: uiContext.menu,
      focus: uiContext.focus
    }
  }), [reduxState, gameContext, uiContext])

  // Combinar todas las acciones
  const gameActions = useMemo(() => ({
    // Acciones del contexto del juego
    game: gameContext.actions,
    
    // Acciones de UI
    ui: uiContext.actions
  }), [gameContext.actions, uiContext.actions])

  return {
    state: gameState,
    actions: gameActions
  }
}

/**
 * Hook especializado para obtener solo la configuración del juego
 */
export const useGameSettings = () => {
  const { settings } = useGameContext()
  const { actions } = useGameContext()
  
  return {
    settings,
    updateSettings: actions
  }
}

/**
 * Hook especializado para obtener solo el estado de UI
 */
export const useGameUI = () => {
  const uiContext = useUIContext()
  
  return {
    ui: {
      modal: uiContext.modal,
      loading: uiContext.loading,
      notification: uiContext.notification,
      cursor: uiContext.cursor,
      dragState: uiContext.dragState,
      menu: uiContext.menu,
      focus: uiContext.focus
    },
    actions: uiContext.actions
  }
}

/**
 * Hook para obtener información del viewport de manera reactiva
 */
export const useViewport = () => {
  const { viewport, actions } = useGameContext()
  
  return {
    viewport,
    updateViewport: actions.updateViewport,
    isMobile: viewport.isMobile,
    isTablet: viewport.width >= 768 && viewport.width < 1024,
    isDesktop: viewport.width >= 1024
  }
}

export default useGame